import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from '@angular/core';
import { DashboardService } from './dashboard.service';
import { DepartmentService } from 'src/app/services/department.service';
import { SyncOverviewDto } from './dashboard.model';
import { Router, RouterLink } from '@angular/router';
import { ModalService } from 'src/app/services/modal.service';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import { NgbTooltip, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { SeverityLevelPipe } from '../../pipes/severity-level.pipe';
import { TimelineEventDto } from '../events/events.models';
import { MainStore } from 'src/app/common/main.store';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, NgbTooltip, NgbAccordionModule, DatePipe, SeverityLevelPipe, FormsModule]
})
export class DashboardComponent {

  private readonly dashboardService = inject(DashboardService);
  private readonly departmentService = inject(DepartmentService);
  private readonly router = inject(Router);
  protected readonly modalService = inject(ModalService);
  protected readonly store = inject(MainStore);

  protected readonly environment = environment.environment;

  private readonly EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

  /** Emits the department id when a valid department is selected, undefined otherwise (keeps resources idle). */
  private readonly departmentId = computed(() => {
    const dept = this.departmentService.selectedDepartment();
    if (!dept?.id || dept.id === this.EMPTY_GUID) return undefined;
    return dept.id;
  });

  protected readonly departmentSelected = computed(() => this.departmentId() != null);

  // Resources — each manages its own loading/error/value state
  protected readonly customerCountResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getCustomerCount(params),
  });

  private readonly companyCountResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getCompanyCount(params),
  });

  protected readonly synchronizationCountResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getSynchronizationCount(params),
  });

  protected readonly resourceAccessesResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getUniqueResourceAccesses(params),
  });

  // Only active for admin users — returns undefined params for non-admins, keeping the resource idle
  protected readonly syncErrorCountResource = resource({
    params: () => this.store.currentUser.get().isAdmin ? {} : undefined,
    loader: () => this.dashboardService.getSyncErrorCount(),
  });

  protected readonly userEventsResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getChangelogs(params, 10, 30),
  });

  protected readonly recentSyncsResource = resource({
    params: () => this.departmentId(),
    loader: ({ params }) => this.dashboardService.getRecentSyncs(params),
  });

  protected readonly userEventsIsLoading = computed(() => this.userEventsResource.isLoading());
  protected readonly userEventsError = computed(() => this.userEventsResource.error() != null);

  protected readonly syncTimeRangeText = computed(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const day = yesterday.getDate().toString().padStart(2, '0');
    const month = (yesterday.getMonth() + 1).toString().padStart(2, '0');
    const year = yesterday.getFullYear();
    const hours = yesterday.getHours().toString().padStart(2, '0');
    const minutes = yesterday.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes} - Nu`;
  });

  // Accordion state (persisted to localStorage via store)
  private readonly _userEventsOpen = signal(this.store.dashboardAccordionState.get().userEvents);
  private readonly _syncOverviewOpen = signal(this.store.dashboardAccordionState.get().syncOverview);

  protected readonly userEventsOpen = this._userEventsOpen.asReadonly();
  protected readonly syncOverviewOpen = this._syncOverviewOpen.asReadonly();

  protected toggleUserEvents(): void {
    this._userEventsOpen.update(v => !v);
    this.store.dashboardAccordionState.set({
      userEvents: this._userEventsOpen(),
      syncOverview: this._syncOverviewOpen(),
    });
  }

  protected toggleSyncOverview(): void {
    this._syncOverviewOpen.update(v => !v);
    this.store.dashboardAccordionState.set({
      userEvents: this._userEventsOpen(),
      syncOverview: this._syncOverviewOpen(),
    });
  }

  protected navigateToEvents(trace: TimelineEventDto): void {
    this.router.navigate([`/events/${trace.itemId}`]);
  }

  protected navigateToResourceAccesses(departmentName: string): void {
    this.router.navigate([`/resourceaccess/${departmentName}`]);
  }

  protected navigateToSync(sync: SyncOverviewDto): void {
    this.router.navigate([`/synchronization/${sync.synchronizationId}`]);
  }

  protected navigateToSyncWithFilter(sync: SyncOverviewDto, status: 'finished' | 'warning' | 'failed', event: Event): void {
    event.stopPropagation();
    this.router.navigate([`/synchronization/${sync.synchronizationId}`], {
      queryParams: { status }
    });
  }
}

